import { useState, useEffect } from 'react';
import { ehNumeroDaCasa, nomeDaConexaoDoNumero, MENSAGEM_NUMERO_DA_CASA } from '@/lib/numerosDaCasa';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription 
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Building2,
  Search,
  User,
  Phone,
  Loader2,
  CheckCircle,
  Database,
  PenLine,
  Users,
  ShieldAlert,
  Lock,
  Crown,
  ArrowRightLeft, X
} from 'lucide-react';
import { SolicitacaoAvaliacaoModal } from '@/components/solicitacao/SolicitacaoAvaliacaoModal';
import { useCNPJLookup } from '@/hooks/useCNPJLookup';
import { useContatoExistente } from '@/hooks/useContatoExistente';
import { useUserProfile } from '@/hooks/useUserProfile';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useQuery } from '@tanstack/react-query';
import { formatarCNPJ, formatarCPF } from '@/utils/formatters';
import { validarCPF } from '@/utils/validators';
import { cn } from '@/lib/utils';
import { canHaveOwnSales } from '@/lib/roles-permissions';

// Tipo de lead fixo para criação manual
const TIPOS_LEAD_MANUAL = [
  {
    value: 'MANUAL' as const,
    label: 'Manual',
    icon: PenLine,
    description: 'Lead adicionado manualmente pelo consultor',
    colorClasses: {
      selected: 'text-success bg-success/5 border-success ring-success',
      icon: 'text-success'
    }
  }
] as const;

type TipoLeadManual = typeof TIPOS_LEAD_MANUAL[number]['value'];

interface ModalCriarLeadManualProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (leadId: string) => void;
  cnpjInicial?: string;
  telefoneInicial?: string;
  razaoSocialInicial?: string;
  origemContexto?: string;
}

// Tipo para resultado do resolver
interface ResolverResult {
  acao_permitida: 'CRIAR' | 'BLOQUEAR_PEDIDO_ANDAMENTO' | 'DIRECIONAR_DONO' | 'SOLICITAR_TRANSFERENCIA';
  pode_criar: boolean;
  mensagem: string;
  detalhes: {
    cnpj: string;
    tem_dono_comercial: boolean;
    owner_consultor_id: string | null;
    owner_nome: string | null;
    cliente_id?: string | null;
    tem_pedido_andamento: boolean;
    pedido_info: any;
    actor_id: string;
    actor_role: string;
    actor_nome: string;
  };
}

interface LeadAbertoRow {
  id: string;
  consultor_id: string | null;
  status_lead: string;
  campanha_id: string | null;
  tipo_lead: string | null;
  pedido_id: string | null;
}

interface ItemQuadroRow {
  pedido_id: string;
  quadro_atual: string;
}

const UF_POR_DDD: Record<string, string> = Object.fromEntries(Object.entries({
  SP: '11 12 13 14 15 16 17 18 19', RJ: '21 22 24', ES: '27 28', MG: '31 32 33 34 35 37 38', PR: '41 42 43 44 45 46',
  SC: '47 48 49', RS: '51 53 54 55', DF: '61', GO: '62 64', TO: '63', MT: '65 66', MS: '67', AC: '68', RO: '69',
  BA: '71 73 74 75 77', SE: '79', PE: '81 87', AL: '82', PB: '83', RN: '84', CE: '85 88', PI: '86 89',
  PA: '91 93 94', AM: '92 97', RR: '95', AP: '96', MA: '98 99',
}).flatMap(([uf, ddds]) => ddds.split(' ').map((ddd) => [ddd, uf])));

export function ModalCriarLeadManual({ open, onOpenChange, onSuccess, cnpjInicial, telefoneInicial, razaoSocialInicial, origemContexto }: ModalCriarLeadManualProps) {
  const { toast } = useToast();
  const { data: userProfile } = useUserProfile();
  const { consultarCNPJ, loading: loadingCNPJ, data: dadosCNPJ, limparResultado, setManualData } = useCNPJLookup();
  const { buscarContatoPorCNPJ, loading: loadingContato } = useContatoExistente();

  // Estados
  const [cnpjInput, setCnpjInput] = useState('');
  const [tipoPessoa, setTipoPessoa] = useState<'PF' | 'PJ'>('PJ');
  const [loadingCPF, setLoadingCPF] = useState(false);
  const [origemDadosCpf, setOrigemDadosCpf] = useState<'crm' | 'assertiva' | null>(null);
  const isPF = tipoPessoa === 'PF';
  const documentoLabel = isPF ? 'CPF' : 'CNPJ';
  const loadingDocumento = loadingCNPJ || loadingCPF;
  const [etapa, setEtapa] = useState<'busca' | 'dados'>('busca');
  const [salvando, setSalvando] = useState(false);
  const [modoBusca, setModoBusca] = useState<'carteira' | 'cnpj'>('cnpj');
  const [buscaCarteira, setBuscaCarteira] = useState('');
  const [contatoEncontradoCRM, setContatoEncontradoCRM] = useState(false);

  // Tipo de lead fixo em MANUAL
  const [tipoLeadSelecionado, setTipoLeadSelecionado] = useState<TipoLeadManual>('MANUAL');

  // Seleção de consultor (gestores)
  const [consultorSelecionadoId, setConsultorSelecionadoId] = useState<string>('');
  const [equipeSelecionadaId, setEquipeSelecionadaId] = useState<string | null>(null);

  // FLUXO OFICIAL P3+P4: Estados do resolver
  const [bloqueio, setBloqueio] = useState<{ tipo: string; mensagem: string } | null>(null);
  const [alertaCpf, setAlertaCpf] = useState<string | null>(null);
  const [solicitarAberto, setSolicitarAberto] = useState(false);
  const [donoComercial, setDonoComercial] = useState<{ id: string; nome: string } | null>(null);
  const [consultorTravado, setConsultorTravado] = useState(false);
  const [diagnostico, setDiagnostico] = useState<ResolverResult | null>(null);
  const [leadEncontrado, setLeadEncontrado] = useState<any>(null);
  // REGRA DE EQUIPE: quem pode abrir/gerenciar o lead encontrado (proprio dono OU
  // gestao da hierarquia — supervisor da equipe, diretoria, master...). consultor = so o seu.
  const [podeGerenciarLead, setPodeGerenciarLead] = useState(false);
  useEffect(() => {
    const cid = leadEncontrado?.consultor_id;
    if (!leadEncontrado) { setPodeGerenciarLead(false); return; }
    if (!cid || cid === userProfile?.id) { setPodeGerenciarLead(true); return; }
    let cancel = false;
    supabase
      .rpc('crm_pode_gerenciar_lead', { p_user_id: userProfile?.id, p_lead_consultor_id: cid })
      .then(({ data }) => { if (!cancel) setPodeGerenciarLead(data === true); });
    return () => { cancel = true; };
  }, [leadEncontrado, userProfile?.id]);

  // Verificar se é vendedor
  const isVendedor = canHaveOwnSales(userProfile?.role);

  // Buscar clientes da carteira do usuário logado (via donos_cliente)
  const { data: clientesFiltrados = [], isLoading: loadingCarteira } = useQuery({
    queryKey: ['clientes-carteira-busca', buscaCarteira, userProfile?.id, tipoPessoa],
    queryFn: async () => {
      const { data } = await supabase
        .from('donos_cliente')
        .select('clientes!inner(id, cnpj, cpf, tipo_pessoa, razao_social, nome_fantasia, classificacao_cliente)')
        .eq('owner_consultor_id', userProfile!.id)
        .eq('clientes.tipo_pessoa', tipoPessoa)
        .order('created_at', { ascending: false })
        .limit(20);

      let resultados = (data || []).map((d: any) => d.clientes).filter(Boolean);

      if (buscaCarteira.length >= 3) {
        const termo = buscaCarteira.toLowerCase();
        resultados = resultados.filter((c: any) =>
          c.razao_social?.toLowerCase().includes(termo) ||
          c.nome_fantasia?.toLowerCase().includes(termo) ||
          c.cnpj?.includes(buscaCarteira) || c.cpf?.includes(buscaCarteira)
        );
      }

      return resultados;
    },
    enabled: modoBusca === 'carteira' && !!userProfile?.id,
  });

  // Buscar consultores ativos
  const { data: consultores = [], isLoading: loadingConsultores } = useQuery({
    queryKey: ['consultores-ativos-criar-lead'],
    queryFn: async () => {
      const { data: usuariosData, error: usuariosError } = await supabase
        .from('usuarios')
        .select('id, nome, foto_url, role, funcao')
        .eq('ativo', true)
        .in('role', ['consultor', 'supervisor'])
        .order('nome');
      
      if (usuariosError) throw usuariosError;
      if (!usuariosData?.length) return [];

      const { data: membrosData } = await supabase
        .from('equipe_membro')
        .select('usuario_id, equipe_id, equipes(id, nome)');

      const membrosMap = new Map();
      (membrosData || []).forEach((m: any) => {
        membrosMap.set(m.usuario_id, {
          equipe_id: m.equipe_id,
          equipe_nome: m.equipes?.nome || null
        });
      });

      return usuariosData.map(u => ({
        id: u.id,
        nome: u.nome,
        foto_url: u.foto_url,
        funcao: (u as { funcao?: string | null }).funcao ?? null,
        equipe_id: membrosMap.get(u.id)?.equipe_id || null,
        equipe_nome: membrosMap.get(u.id)?.equipe_nome || 'Sem equipe'
      }));
    },
    enabled: open && !isVendedor,
  });

  // CONSULTOR RETENÇÃO não tem regra de dono (Douglas 09/10/2026): o lead fica
  // com ele e o dono do cliente não muda. Vale para quem está logado como ele e
  // para quem escolhe ele como consultor, mesmo com o cliente tendo dono.
  const retencao = consultores.find((c) => c.funcao === 'CONSULTOR_RETENCAO') ?? null;
  const souRetencao = userProfile?.funcao === 'CONSULTOR_RETENCAO';
  const destinoRetencaoId = souRetencao
    ? (userProfile?.id ?? null)
    : (retencao && consultorSelecionadoId === retencao.id ? retencao.id : null);
  const direcionadoAoDono = donoComercial && !destinoRetencaoId ? donoComercial : null;

  // Dados do contato real
  const [contatoNome, setContatoNome] = useState('');
  // 67d — situação atual (alimenta a análise da aba DADOS)
  const [operadoraAtualSel, setOperadoraAtualSel] = useState('');
  const [obsLivre, setObsLivre] = useState('');
  const [contatoTelefone, setContatoTelefone] = useState('');
  const [contatoCargo, setContatoCargo] = useState('');
  const [contatoEmail, setContatoEmail] = useState('');

  // Dados editáveis da empresa — rodada 2: a Receita PREENCHE estes campos
  // (nunca card espelho); quando a API falha, o usuário completa aqui mesmo.
  const [editRazaoSocial, setEditRazaoSocial] = useState('');
  const [editNomeFantasia, setEditNomeFantasia] = useState('');
  const [editCidade, setEditCidade] = useState('');
  const [editUF, setEditUF] = useState('');
  const [editCEP, setEditCEP] = useState('');

  // Situação atual — 1 linha de 4 campos (rodada 2). Meses contrato e Proposta
  // R$ saíram: proposta se monta na aba PROPOSTA do lead, não na criação.
  const [linhas, setLinhas] = useState('');
  const [valorAtual, setValorAtual] = useState('');
  const [interesseSel, setInteresseSel] = useState('');

  // Reset ao fechar
  useEffect(() => {
    if (!open) {
      setTipoPessoa('PJ');
      setCnpjInput('');
      setEtapa('busca');
      limparResultado();
      setTipoLeadSelecionado('MANUAL');
      setConsultorSelecionadoId('');
      setEquipeSelecionadaId(null);
      setContatoNome('');
      setContatoTelefone('');
      setContatoCargo('');
      setContatoEmail('');
      setLinhas('');
      setValorAtual('');
      setInteresseSel('');
      setContatoEncontradoCRM(false);
      setBloqueio(null);
      setAlertaCpf(null);
    setAlertaCpf(null);
      setDonoComercial(null);
      setConsultorTravado(false);
      setModoBusca('cnpj');
      setBuscaCarteira('');
      setEditRazaoSocial('');
      setEditNomeFantasia('');
      setEditCidade('');
      setEditUF('');
      setEditCEP('');
    }
  }, [open, limparResultado]);

  // Rodada 2: a Receita preenche os CAMPOS das seções Empresa/Contato — nunca
  // um card espelho (mostrar duas vezes é o que dobrava a altura do modal).
  useEffect(() => {
    const d = dadosCNPJ?.data;
    if (d && d.razao_social) {
      setEditRazaoSocial(d.razao_social);
      setEditNomeFantasia(d.nome_fantasia && d.nome_fantasia !== d.razao_social ? d.nome_fantasia : '');
      setEditCidade(d.endereco_cidade || '');
      setEditUF(d.endereco_uf || '');
      setEditCEP(d.endereco_cep_formatado || d.endereco_cep || '');
    }
  }, [dadosCNPJ]);

  // Auto-prefill quando vem do WhatsApp (cnpjInicial)
  const [autoBuscaPendente, setAutoBuscaPendente] = useState(false);
  
  useEffect(() => {
    if (open && cnpjInicial) {
      const cnpjLimpo = cnpjInicial.replace(/\D/g, '');
      if (cnpjLimpo.length === 14 || cnpjLimpo.length === 11) {
        setTipoPessoa(cnpjLimpo.length === 11 ? 'PF' : 'PJ');
        setCnpjInput(cnpjLimpo.length === 11 ? formatarCPF(cnpjLimpo) : formatarCNPJ(cnpjLimpo));
        if (telefoneInicial) {
          setContatoTelefone(telefoneInicial);
        }
        setAutoBuscaPendente(true);
      }
    }
  }, [open, cnpjInicial]);

  useEffect(() => {
    if (autoBuscaPendente && cnpjInput && !loadingCNPJ) {
      setAutoBuscaPendente(false);
      handleBuscarCNPJ();
    }
  }, [autoBuscaPendente, cnpjInput]);

  // Handler para selecionar cliente da carteira
  const handleSelecionarClienteCarteira = async (cliente: { id: string; cnpj: string; cpf?: string | null; razao_social: string; nome_fantasia: string | null; classificacao_cliente: string | null }) => {
    if (cliente.cpf) {
      setTipoPessoa('PF');
      setCnpjInput(formatarCPF(cliente.cpf));
      setModoBusca('cnpj');
      await handleBuscarCPF(cliente.cpf);
      return;
    }
    const cnpjLimpo = cliente.cnpj.replace(/\D/g, '');
    setCnpjInput(formatarCNPJ(cnpjLimpo));

    // Buscar dados completos do cliente
    const { data: clienteLocal } = await supabase
      .from('clientes')
      .select('cnpj, razao_social, nome_fantasia, telefone_principal, email, endereco, cnae_principal, cnae_descricao, situacao_cadastral, data_inicio_atividade')
      .eq('id', cliente.id)
      .maybeSingle();

    if (clienteLocal) {
      const dadosLocais = {
        cnpj: clienteLocal.cnpj,
        razao_social: clienteLocal.razao_social,
        nome_fantasia: clienteLocal.nome_fantasia || '',
        situacao_cadastral: clienteLocal.situacao_cadastral || '',
        data_inicio_atividade: clienteLocal.data_inicio_atividade || '',
        cnae_principal: clienteLocal.cnae_principal || '',
        cnae_descricao: clienteLocal.cnae_descricao || '',
        telefone_principal: clienteLocal.telefone_principal || '',
        telefone_formatado: '',
        telefone2: '',
        telefone2_formatado: '',
        email_principal: clienteLocal.email || '',
        endereco_rua: '',
        endereco_numero: '',
        endereco_bairro: '',
        endereco_cep: '',
        endereco_cep_formatado: '',
        endereco_cidade: '',
        endereco_uf: '',
        mei: false,
        porte: '',
        natureza_juridica: '',
        socios: [] as any[],
      };

      setManualData({
        data: dadosLocais,
        source: 'cache',
        atualizado_em: new Date().toISOString(),
      });

      setEtapa('dados');

      // Chamar resolver
      if (userProfile?.id) {
        try {
          const { data: resolverResult, error: resolverError } = await supabase.rpc(
            'crm_resolver_regras_cnpj',
            { p_cnpj: cnpjLimpo, p_actor_id: userProfile.id, p_origem: 'lead_manual' }
          );

          if (!resolverError && resolverResult) {
            const r = resolverResult as unknown as ResolverResult;
            setDiagnostico(r);
            if (r.acao_permitida === 'BLOQUEAR_PEDIDO_ANDAMENTO') {
              setBloqueio({ tipo: 'PEDIDO_ANDAMENTO', mensagem: r.mensagem });
            } else if (r.acao_permitida === 'SOLICITAR_TRANSFERENCIA') {
              setBloqueio({
                tipo: 'DONO_OUTRO_CONSULTOR',
                mensagem: r.mensagem || `Este CNPJ pertence ao consultor ${r.detalhes?.owner_nome || ''}. Solicite a transferência ao seu gestor.`
              });
            } else if (r.acao_permitida === 'DIRECIONAR_DONO') {
              const ownerId = r.detalhes?.owner_consultor_id;
              const ownerNome = r.detalhes?.owner_nome || 'Dono Comercial';
              setDonoComercial({ id: ownerId!, nome: ownerNome });
              setConsultorSelecionadoId(ownerId!);
              const consultor = consultores?.find(c => c.id === ownerId);
              if (consultor) setEquipeSelecionadaId(consultor.equipe_id);
              setConsultorTravado(true);
            }
          }
        } catch (err) {
          console.error('Erro ao chamar resolver:', err);
        }
      }

      // Buscar contato existente
      const contatoExistente = await buscarContatoPorCNPJ(cnpjLimpo);
      if (contatoExistente) {
        setContatoNome(contatoExistente.nome);
        setContatoTelefone(formatarTelefoneExibicao(contatoExistente.telefone));
        setContatoCargo(contatoExistente.cargo);
        setContatoEmail(contatoExistente.email);
        setContatoEncontradoCRM(true);
      }
    }
  };

  // Formatar telefone para exibição
  const formatarTelefoneExibicao = (telefone: string): string => {
    const numeros = telefone.replace(/\D/g, '').slice(0, 11);
    if (numeros.length > 6) {
      return `(${numeros.slice(0, 2)}) ${numeros.slice(2, numeros.length - 4)}-${numeros.slice(-4)}`;
    } else if (numeros.length > 2) {
      return `(${numeros.slice(0, 2)}) ${numeros.slice(2)}`;
    } else if (numeros.length > 0) {
      return `(${numeros}`;
    }
    return numeros;
  };

  // Buscar CNPJ — agora com resolver do backend
  const handleBuscarCPF = async (documento = cnpjInput) => {
    const cpf = documento.replace(/\D/g, '');
    if (!validarCPF(cpf) || !userProfile?.id) {
      toast({ title: 'Informe um CPF válido', variant: 'destructive' });
      return;
    }
    setLoadingCPF(true);
    setBloqueio(null);
    setAlertaCpf(null);
    setDonoComercial(null);
    setConsultorTravado(false);
    setDiagnostico(null);
    setLeadEncontrado(null);
    try {
      // Mesma política do CNPJ; CPF não vai para Receita nem para coluna CNPJ.
      const [resolucao, cliente, leads] = await Promise.all([
        supabase.rpc('crm_resolver_regras_cnpj', { p_cnpj: cpf, p_actor_id: userProfile.id, p_origem: 'lead_manual' }),
        supabase.from('clientes').select('razao_social, telefone_principal, email').eq('cpf', cpf).maybeSingle(),
        supabase.from('leads_carteira').select('id, consultor_id, status_lead, campanha_id, tipo_lead').eq('cpf', cpf)
          .not('status_lead', 'in', '(CONVERTIDO,CARTEIRIZADO,NEGADO,SAIU_CARTEIRA,DESCARTADO,VENCIDO,REVISANDO)')
          .order('updated_at', { ascending: false }),
      ]);
      if (resolucao.error) throw resolucao.error;
      if (cliente.error) throw cliente.error;
      if (leads.error) throw leads.error;
      const r = resolucao.data as unknown as ResolverResult;
      setDiagnostico(r);
      setLeadEncontrado(leads.data?.[0] || null);
      if (!r.pode_criar) setBloqueio({ tipo: r.acao_permitida, mensagem: r.mensagem });
      if (r.acao_permitida === 'DIRECIONAR_DONO' && r.detalhes.owner_consultor_id) {
        setDonoComercial({ id: r.detalhes.owner_consultor_id, nome: r.detalhes.owner_nome || 'Dono Comercial' });
        setConsultorSelecionadoId(r.detalhes.owner_consultor_id);
        setConsultorTravado(true);
      }
      const local = cliente.data;
      // CPF não tem Receita pública: quem preenche é a Assertiva, consultada UMA vez por CPF
      // (CPF já consultado volta do banco, sem nova cobrança).
      let pf: { nome: string; telefone: string; email: string; uf: string; cidade: string; cep: string; situacao: string; obito: boolean } | null = null;
      if (!local?.razao_social) {
        const { data: ass } = await supabase.functions.invoke('assertiva-consulta', { body: { tipo: 'cpf', chave: cpf, finalidade: 4 } });
        const resp = (ass as { resultado?: { resposta?: {
          dadosCadastrais?: { nome?: string; situacaoCadastral?: string; obitoProvavel?: boolean };
          telefones?: { moveis?: { numero?: string }[]; fixos?: { numero?: string }[] };
          emails?: { email?: string }[];
          enderecos?: { uf?: string; cidade?: string; cep?: string }[];
        } } } | null)?.resultado?.resposta;
        if (resp?.dadosCadastrais?.nome) {
          const tel = resp.telefones?.moveis?.[0]?.numero || resp.telefones?.fixos?.[0]?.numero || '';
          // A Assertiva lista vários endereços sem dizer qual é o atual: fica o do estado do DDD do celular.
          const ufDoDdd = UF_POR_DDD[String(tel).replace(/\D/g, '').slice(0, 2)];
          const end = resp.enderecos?.find((e) => ufDoDdd && e.uf === ufDoDdd) || resp.enderecos?.[0] || { uf: '', cidade: '', cep: '' };
          pf = {
            nome: String(resp.dadosCadastrais.nome), telefone: String(tel).replace(/\D/g, ''), email: String(resp.emails?.[0]?.email || ''),
            uf: String(end.uf || ''), cidade: String(end.cidade || ''), cep: String(end.cep || ''),
            situacao: String(resp.dadosCadastrais.situacaoCadastral || ''), obito: resp.dadosCadastrais.obitoProvavel === true,
          };
        }
      }
      setOrigemDadosCpf(local?.razao_social ? 'crm' : pf ? 'assertiva' : null);
      if (pf?.obito) {
        setBloqueio({ tipo: 'CPF_IRREGULAR', mensagem: 'CPF com óbito provável na Assertiva — não abrir lead.' });
      } else if (pf?.situacao && pf.situacao.toUpperCase() !== 'REGULAR') {
        setAlertaCpf(`CPF ${pf.situacao} na Receita — a venda pode seguir; confira os dados do titular.`);
      }
      setManualData({ source: 'cache', atualizado_em: new Date().toISOString(), data: {
        cnpj: '', razao_social: local?.razao_social || pf?.nome || '', nome_fantasia: '',
        situacao_cadastral: pf?.situacao || '', data_inicio_atividade: '', cnae_principal: '', cnae_descricao: '',
        telefone_principal: local?.telefone_principal || pf?.telefone || '', telefone_formatado: '', telefone2: '', telefone2_formatado: '',
        email_principal: local?.email || pf?.email || '', endereco_rua: '', endereco_numero: '', endereco_bairro: '',
        endereco_cep: pf?.cep || '', endereco_cep_formatado: pf?.cep || '', endereco_cidade: pf?.cidade || '', endereco_uf: pf?.uf || '',
        mei: false, porte: '', natureza_juridica: '', socios: [],
      } });
      setContatoNome(local?.razao_social || pf?.nome || razaoSocialInicial || '');
      setContatoCargo('Titular');
      const telefoneCpf = local?.telefone_principal || pf?.telefone;
      if (telefoneCpf) setContatoTelefone(telefoneCpf);
      const emailCpf = local?.email || pf?.email;
      if (emailCpf) setContatoEmail(emailCpf);
      setEtapa('dados');
    } catch (error) {
      limparResultado();
      setBloqueio({ tipo: 'CONSULTA', mensagem: 'Não foi possível conferir as regras do cliente.' });
      toast({ title: 'Não foi possível consultar o CPF', description: error instanceof Error ? error.message : 'Tente novamente.', variant: 'destructive' });
    } finally {
      setLoadingCPF(false);
    }
  };

  const handleBuscarCNPJ = async () => {
    if (isPF) return handleBuscarCPF();
    const cnpjLimpo = cnpjInput.replace(/\D/g, '');
    
    if (cnpjLimpo.length !== 14) {
      toast({ title: 'CNPJ deve ter 14 dígitos', variant: 'destructive' });
      return;
    }

    // Reset estados do resolver
    setBloqueio(null);
    setAlertaCpf(null);
    setDonoComercial(null);
    setConsultorTravado(false);
    setDiagnostico(null);

    // (a) DIAGNOSTICO RAPIDO: roda o resolver em PARALELO, sem esperar a consulta lenta de CNPJ
    if (userProfile?.id) {
      Promise.resolve(supabase.rpc('crm_resolver_regras_cnpj', { p_cnpj: cnpjLimpo, p_actor_id: userProfile.id, p_origem: 'lead_manual' }))
        .then(({ data, error }: any) => { if (!error && data) setDiagnostico(data as unknown as ResolverResult); })
        .catch(() => {});
    }
    setLeadEncontrado(null);
    Promise.resolve(supabase.from('leads_carteira')
      .select('id, consultor_id, status_lead, campanha_id, tipo_lead')
      .eq('cnpj', cnpjLimpo)
      .not('status_lead', 'in', `(CONVERTIDO,CARTEIRIZADO,NEGADO,SAIU_CARTEIRA,DESCARTADO,VENCIDO,REVISANDO)`))
      .then(({ data }: any) => {
        const PRI: Record<string, number> = { PROPOSTA: 5, NEGOCIANDO: 4, TENTANDO_CONTATO: 3, ATRIBUIDO: 2, DISPONIVEL: 1 };
        setLeadEncontrado(((data as any[]) || []).slice().sort((a, b) => (PRI[b.status_lead] || 0) - (PRI[a.status_lead] || 0))[0] || null);
      }).catch(() => {});

    const resultado = await consultarCNPJ(cnpjLimpo);
    
    const notFound = resultado && (resultado as any).not_found;

    if (resultado && !notFound) {
      setEtapa('dados');
      
      // FLUXO OFICIAL P3+P4: Chamar resolver do backend
      if (userProfile?.id) {
        try {
          const { data: resolverResult, error: resolverError } = await supabase.rpc(
            'crm_resolver_regras_cnpj',
            {
              p_cnpj: cnpjLimpo,
              p_actor_id: userProfile.id,
              p_origem: 'lead_manual'
            }
          );

          if (!resolverError && resolverResult) {
            const r = resolverResult as unknown as ResolverResult;
            setDiagnostico(r);
            
            if (r.acao_permitida === 'BLOQUEAR_PEDIDO_ANDAMENTO') {
              setBloqueio({
                tipo: 'PEDIDO_ANDAMENTO',
                mensagem: r.mensagem
              });
            } else if (r.acao_permitida === 'SOLICITAR_TRANSFERENCIA') {
              setBloqueio({
                tipo: 'DONO_OUTRO_CONSULTOR',
                mensagem: r.mensagem || 
                  `Este CNPJ pertence ao consultor ${r.detalhes?.owner_nome || ''}. Solicite a transferência ao seu gestor.`
              });
            } else if (r.acao_permitida === 'DIRECIONAR_DONO') {
              const ownerId = r.detalhes?.owner_consultor_id;
              const ownerNome = r.detalhes?.owner_nome || 'Dono Comercial';
              setDonoComercial({ id: ownerId!, nome: ownerNome });
              setConsultorSelecionadoId(ownerId!);
              const consultor = consultores?.find(c => c.id === ownerId);
              if (consultor) {
                setEquipeSelecionadaId(consultor.equipe_id);
              }
              setConsultorTravado(true);
            }
          }
        } catch (err) {
          console.error('Erro ao chamar resolver:', err);
        }
      }

      // Buscar contato existente no CRM
      const contatoExistente = await buscarContatoPorCNPJ(cnpjLimpo);
      
      if (contatoExistente) {
        setContatoNome(contatoExistente.nome);
        setContatoTelefone(formatarTelefoneExibicao(contatoExistente.telefone));
        setContatoCargo(contatoExistente.cargo);
        setContatoEmail(contatoExistente.email);
        setContatoEncontradoCRM(true);
        
        toast({
          title: 'Contato encontrado no CRM',
          description: `${contatoExistente.nome} (${contatoExistente.fonte === 'leads_carteira' ? 'lead anterior' : 'cliente'})`,
        });
      } else {
        const socios = resultado.data.socios || [];
        const admin = socios.find((s: any) => s.isAdministrador);
        if (admin) {
          setContatoNome(admin.nome || '');
        }
        setContatoEncontradoCRM(false);
      }
    } else {
      // FALLBACK LOCAL: buscar na tabela 'clientes' do CRM
      try {
        const { data: clienteLocal } = await supabase
          .from('clientes')
          .select('cnpj, razao_social, nome_fantasia, telefone_principal, email, endereco, cnae_principal, cnae_descricao, situacao_cadastral, data_inicio_atividade')
          .eq('cnpj', cnpjLimpo)
          .maybeSingle();

        if (clienteLocal) {
          const dadosLocais = {
            cnpj: clienteLocal.cnpj,
            razao_social: clienteLocal.razao_social,
            nome_fantasia: clienteLocal.nome_fantasia || '',
            situacao_cadastral: clienteLocal.situacao_cadastral || '',
            data_inicio_atividade: clienteLocal.data_inicio_atividade || '',
            cnae_principal: clienteLocal.cnae_principal || '',
            cnae_descricao: clienteLocal.cnae_descricao || '',
            telefone_principal: clienteLocal.telefone_principal || '',
            telefone_formatado: '',
            telefone2: '',
            telefone2_formatado: '',
            email_principal: clienteLocal.email || '',
            endereco_rua: '',
            endereco_numero: '',
            endereco_bairro: '',
            endereco_cep: '',
            endereco_cep_formatado: '',
            endereco_cidade: '',
            endereco_uf: '',
            mei: false,
            porte: '',
            natureza_juridica: '',
            socios: [] as any[],
          };

          setManualData({
            data: dadosLocais,
            source: 'cache',
            atualizado_em: new Date().toISOString(),
          });

          setEtapa('dados');

          toast({
            title: 'Dados obtidos do CRM interno',
            description: 'CNPJ não encontrado na Receita Federal, usando dados do CRM.',
          });

          // Chamar resolver normalmente
          if (userProfile?.id) {
            try {
              const { data: resolverResult, error: resolverError } = await supabase.rpc(
                'crm_resolver_regras_cnpj',
                {
                  p_cnpj: cnpjLimpo,
                  p_actor_id: userProfile.id,
                  p_origem: 'lead_manual'
                }
              );

              if (!resolverError && resolverResult) {
                const r = resolverResult as unknown as ResolverResult;
            setDiagnostico(r);
                
                if (r.acao_permitida === 'BLOQUEAR_PEDIDO_ANDAMENTO') {
                  setBloqueio({
                    tipo: 'PEDIDO_ANDAMENTO',
                    mensagem: r.mensagem
                  });
                } else if (r.acao_permitida === 'SOLICITAR_TRANSFERENCIA') {
                  setBloqueio({
                    tipo: 'DONO_OUTRO_CONSULTOR',
                    mensagem: r.mensagem || 
                      `Este CNPJ pertence ao consultor ${r.detalhes?.owner_nome || ''}. Solicite a transferência ao seu gestor.`
                  });
                } else if (r.acao_permitida === 'DIRECIONAR_DONO') {
                  const ownerId = r.detalhes?.owner_consultor_id;
                  const ownerNome = r.detalhes?.owner_nome || 'Dono Comercial';
                  setDonoComercial({ id: ownerId!, nome: ownerNome });
                  setConsultorSelecionadoId(ownerId!);
                  const consultor = consultores?.find(c => c.id === ownerId);
                  if (consultor) {
                    setEquipeSelecionadaId(consultor.equipe_id);
                  }
                  setConsultorTravado(true);
                }
              }
            } catch (err) {
              console.error('Erro ao chamar resolver:', err);
            }
          }

          // Buscar contato existente no CRM
          const contatoExistente = await buscarContatoPorCNPJ(cnpjLimpo);
          if (contatoExistente) {
            setContatoNome(contatoExistente.nome);
            setContatoTelefone(formatarTelefoneExibicao(contatoExistente.telefone));
            setContatoCargo(contatoExistente.cargo);
            setContatoEmail(contatoExistente.email);
            setContatoEncontradoCRM(true);
          }
        } else {
          // FALLBACK: buscar em leads_carteira antes de permitir preenchimento manual
          let dadosFallback: any = null;
          try {
            const { data: leadAnterior } = await supabase
              .from('leads_carteira')
              .select('razao_social, nome_fantasia, telefone, email, endereco_cidade, endereco_uf')
              .eq('cnpj', cnpjLimpo)
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle();
            
            if (leadAnterior) {
              dadosFallback = leadAnterior;
            }
          } catch (err) {
            console.error('Erro ao buscar fallback em leads_carteira:', err);
          }

          const dadosManuais = {
            cnpj: cnpjLimpo,
            razao_social: dadosFallback?.razao_social || '',
            nome_fantasia: dadosFallback?.nome_fantasia || '',
            situacao_cadastral: '',
            data_inicio_atividade: '',
            cnae_principal: '',
            cnae_descricao: '',
            telefone_principal: dadosFallback?.telefone || '',
            telefone_formatado: '',
            telefone2: '',
            telefone2_formatado: '',
            email_principal: dadosFallback?.email || '',
            endereco_rua: '',
            endereco_numero: '',
            endereco_bairro: '',
            endereco_cep: '',
            endereco_cep_formatado: '',
            endereco_cidade: dadosFallback?.endereco_cidade || '',
            endereco_uf: dadosFallback?.endereco_uf || '',
            mei: false,
            porte: '',
            natureza_juridica: '',
            socios: [] as any[],
          };

          setManualData({
            data: dadosManuais,
            source: 'cache',
            atualizado_em: new Date().toISOString(),
          });

          setEtapa('dados');

          toast({
            title: dadosFallback ? 'Dados parciais encontrados' : 'CNPJ não localizado nas fontes',
            description: dadosFallback 
              ? 'Usando dados de um lead anterior. Verifique e complete as informações.'
              : 'Preencha os dados manualmente para continuar.',
          });

          // Chamar resolver normalmente
          if (userProfile?.id) {
            try {
              const { data: resolverResult, error: resolverError } = await supabase.rpc(
                'crm_resolver_regras_cnpj',
                {
                  p_cnpj: cnpjLimpo,
                  p_actor_id: userProfile.id,
                  p_origem: 'lead_manual'
                }
              );

              if (!resolverError && resolverResult) {
                const r = resolverResult as unknown as ResolverResult;
            setDiagnostico(r);
                
                if (r.acao_permitida === 'BLOQUEAR_PEDIDO_ANDAMENTO') {
                  setBloqueio({
                    tipo: 'PEDIDO_ANDAMENTO',
                    mensagem: r.mensagem
                  });
                } else if (r.acao_permitida === 'SOLICITAR_TRANSFERENCIA') {
                  setBloqueio({
                    tipo: 'DONO_OUTRO_CONSULTOR',
                    mensagem: r.mensagem || 
                      `Este CNPJ pertence ao consultor ${r.detalhes?.owner_nome || ''}. Solicite a transferência ao seu gestor.`
                  });
                } else if (r.acao_permitida === 'DIRECIONAR_DONO') {
                  const ownerId = r.detalhes?.owner_consultor_id;
                  const ownerNome = r.detalhes?.owner_nome || 'Dono Comercial';
                  setDonoComercial({ id: ownerId!, nome: ownerNome });
                  setConsultorSelecionadoId(ownerId!);
                  const consultor = consultores?.find(c => c.id === ownerId);
                  if (consultor) {
                    setEquipeSelecionadaId(consultor.equipe_id);
                  }
                  setConsultorTravado(true);
                }
              }
            } catch (err) {
              console.error('Erro ao chamar resolver:', err);
            }
          }

          // Buscar contato existente no CRM
          const contatoExistente = await buscarContatoPorCNPJ(cnpjLimpo);
          if (contatoExistente) {
            setContatoNome(contatoExistente.nome);
            setContatoTelefone(formatarTelefoneExibicao(contatoExistente.telefone));
            setContatoCargo(contatoExistente.cargo);
            setContatoEmail(contatoExistente.email);
            setContatoEncontradoCRM(true);
          }
        }
      } catch (err) {
        console.error('Erro no fallback local:', err);
        toast({
          title: 'Erro ao buscar CNPJ',
          description: 'Ocorreu um erro ao tentar localizar este CNPJ.',
          variant: 'destructive'
        });
      }
    }
  };

  // Formatar telefone no input
  const handleTelefoneChange = (valor: string) => {
    const numeros = valor.replace(/\D/g, '').slice(0, 11);
    let formatado = numeros;
    
    if (numeros.length > 6) {
      formatado = `(${numeros.slice(0, 2)}) ${numeros.slice(2, numeros.length - 4)}-${numeros.slice(-4)}`;
    } else if (numeros.length > 2) {
      formatado = `(${numeros.slice(0, 2)}) ${numeros.slice(2)}`;
    } else if (numeros.length > 0) {
      formatado = `(${numeros}`;
    }
    
    setContatoTelefone(formatado);
  };

  const dados = dadosCNPJ?.data;

  // Flag: dados da empresa precisam ser preenchidos manualmente
  const dadosManuaisEditaveis = !!(dados && !dados.razao_social);

  // Validar dados obrigatórios
  const dadosValidos = () => {
    // Se há bloqueio, não pode salvar
    if (bloqueio || (isPF && !validarCPF(cnpjInput))) return false;
    
    const consultorValido = isVendedor || consultorSelecionadoId !== '';

    // Validação da empresa: se modo manual, exigir campos editáveis preenchidos
    const empresaValida = (dadosManuaisEditaveis || isPF)
      ? (editRazaoSocial.trim().length >= 3 && editCidade.trim().length >= 2 && editUF.length === 2)
      : !!(dados?.razao_social);
    
    return (
      empresaValida &&
      consultorValido &&
      contatoNome.trim().length >= 3 &&
      contatoTelefone.replace(/\D/g, '').length >= 10 &&
      contatoCargo.trim().length >= 2 &&
      contatoEmail.includes('@')
    );
  };

  // Salvar lead — com double-check via resolver
  const handleSalvar = async () => {
    if (!dadosCNPJ?.data || !userProfile?.id) return;

    if (!dadosValidos()) {
      toast({
        title: 'Preencha todos os campos obrigatórios',
        description: 'Tipo de lead, Nome, Telefone, Cargo e Email são obrigatórios.',
        variant: 'destructive'
      });
      return;
    }

    setSalvando(true);

    try {
      const cnpjLimpo = cnpjInput.replace(/\D/g, '');

      // ========================================================================
      // REGRA CRM nº 3: Buscar campanha MANUAL obrigatória antes de tudo
      // ========================================================================
      const { data: campanhaManuais } = await supabase
        .from('campanhas_prospeccao')
        .select('id')
        .eq('tipo_lead', 'MANUAL')
        .eq('ativa', true)
        .limit(1)
        .maybeSingle();

      if (!campanhaManuais) {
        toast({
          title: 'Campanha MANUAL não configurada',
          description: 'Regra CRM nº 3: leads manuais requerem a campanha MANUAL ativa. Contate o administrador.',
          variant: 'destructive'
        });
        setSalvando(false);
        return;
      }

      const campanhaManualId = campanhaManuais.id;

      // Contato Real é o telefone DO CLIENTE — linha nossa aqui vira proposta
      // enviada para nós mesmos (ver lib/numerosDaCasa.ts).
      if (await ehNumeroDaCasa(contatoTelefone)) {
        const conexao = await nomeDaConexaoDoNumero(contatoTelefone);
        toast({
          title: 'Esse número é do CRM, não do cliente',
          description: conexao
            ? `${MENSAGEM_NUMERO_DA_CASA} (linha "${conexao}")`
            : MENSAGEM_NUMERO_DA_CASA,
          variant: 'destructive'
        });
        setSalvando(false);
        return;
      }

      // FLUXO OFICIAL: Validação via RPC Enforcer (impede bypass via API)
      const consultorIdFinalValidacao = destinoRetencaoId || donoComercial?.id || consultorSelecionadoId || userProfile.id;
      const { error: validacaoError } = await supabase.rpc('crm_validar_criacao_lead', {
        p_cnpj: cnpjLimpo,
        p_actor_id: userProfile.id,
        p_origem: 'lead_manual',
        p_consultor_destino_id: consultorIdFinalValidacao
      });

      if (validacaoError) {
        toast({
          title: 'Bloqueado pelo Fluxo Oficial',
          description: validacaoError.message,
          variant: 'destructive'
        });
        setSalvando(false);
        return;
      }

      const dadosBase = dadosCNPJ.data;
      // Rodada 2: os campos Empresa são a fonte — a Receita só os pré-preenche.
      // O que o usuário ajustou nos campos vale sobre o retorno da API.
      const dados = {
        ...dadosBase,
        razao_social: editRazaoSocial.trim() || dadosBase.razao_social,
        nome_fantasia: editNomeFantasia.trim() || dadosBase.nome_fantasia || editRazaoSocial.trim(),
        endereco_cidade: editCidade.trim() || dadosBase.endereco_cidade,
        endereco_uf: editUF || dadosBase.endereco_uf,
        situacao_cadastral: dadosBase.situacao_cadastral || 'Não consultada',
      };
      const now = new Date();
      const importacaoId = crypto.randomUUID();

      // Para vendedores, SEMPRE usar o próprio ID (RLS exige consultor_id = auth.uid())
      const consultorIdFinal = destinoRetencaoId || donoComercial?.id || (isVendedor
        ? userProfile.id
        : (consultorSelecionadoId || userProfile.id));

      // REGRA CORE: equipe_id sempre acompanha o consultor_id final.
      // Nunca herdar a equipe do criador (BKO/Qualidade) para um lead que pertence
      // a um consultor de outra equipe. O trigger trg_sync_equipe_leads_carteira
      // também garante isso no banco como defesa em profundidade.
      const consultorFinal = consultores?.find(c => c.id === consultorIdFinal);
      const equipeIdFinal =
        consultorFinal?.equipe_id
        ?? (consultorIdFinal === userProfile.id ? userProfile.equipe_id : null);
      const origemObs = destinoRetencaoId
        ? `Origem: Inclusão Manual (${tipoLeadSelecionado}) — Para o Consultor Retenção${donoComercial ? ` (o dono do cliente segue ${donoComercial.nome})` : ''}`
        : direcionadoAoDono
          ? `Origem: Inclusão Manual (${tipoLeadSelecionado}) — Direcionado ao Dono Comercial: ${direcionadoAoDono.nome}`
          : `Origem: Inclusão Manual (${tipoLeadSelecionado})`;

      // Montar endereço
      const endereco = [
        dados.endereco_rua,
        dados.endereco_numero,
        dados.endereco_bairro,
        dados.endereco_cidade,
        dados.endereco_uf
      ].filter(Boolean).join(', ');

      // ========================================================================
      // REGRA CRM nº 2: Buscar lead em aberto por campanha MANUAL + CNPJ
      // (não mais por tipo_lead + mes_referencia)
      // ========================================================================
      // REVISANDO = status transitório interno (máx 2h). Se um lead ficou preso
      // em REVISANDO, não deve bloquear a criação de um novo lead manual.
      const STATUS_FINALIZADOS = ['CONVERTIDO', 'CARTEIRIZADO', 'NEGADO', 'SAIU_CARTEIRA', 'DESCARTADO', 'VENCIDO', 'REVISANDO'];
      // LEI DE LEAD (Douglas 17/06): buscar lead aberto em QUALQUER campanha (nao so MANUAL).
      // Se ja existe lead aberto, abre/reaproveita o existente DO TIPO QUE FOR, priorizando o que
      // esta sendo trabalhado (PROPOSTA/NEGOCIANDO) sobre DISPONIVEL. So cria MANUAL novo se nao houver nenhum.
      const { data: leadsAbertosData } = await supabase
        .from('leads_carteira')
        .select('id, consultor_id, status_lead, campanha_id, tipo_lead, pedido_id')
        .eq(isPF ? 'cpf' : 'cnpj', cnpjLimpo)
        .not('status_lead', 'in', `(${STATUS_FINALIZADOS.join(',')})`);
      const leadsAbertosRaw = (leadsAbertosData || []) as unknown as LeadAbertoRow[];

      // Douglas 03/08: lead preso a PEDIDO CANCELADO pertence a quem teve o cancelamento
      // (a recuperacao e' dele) e NUNCA e' reaproveitado por outro consultor -- quem e'
      // dono da carteira hoje cria o lead DELE por cima, em vez de herdar o cancelado do
      // antecessor. Sem isso o dono novo ficava travado: ou tomava "lead de outro
      // consultor", ou reaproveitava um lead que abre o pedido cancelado alheio.
      // Cancelado se le' pelos ITENS -- pedido sem item (linha fantasma) nao conta.
      const pedidoIdsAbertos = [...new Set(
        leadsAbertosRaw.map((l) => l.pedido_id).filter((id): id is string => !!id)
      )];
      const pedidosCancelados = new Set<string>();
      const pedidosVivos = new Set<string>();
      // Pedido que TEM item. O que nao esta aqui e' casca vazia — ver o filtro abaixo.
      const pedidosComItem = new Set<string>();
      let leituraDeItensOk = false;
      if (pedidoIdsAbertos.length > 0) {
        const { data: itensData, error: itensErro } = await supabase
          .from('itens_pedido')
          .select('pedido_id, quadro_atual')
          .in('pedido_id', pedidoIdsAbertos);
        // Só confiamos na conclusão "pedido vazio" se a leitura funcionou. Erro de
        // consulta não é estado: sem isto, uma falha de rede faria TODO lead virar
        // casca vazia e liberaria criação em cima de lead legítimo.
        leituraDeItensOk = !itensErro;
        const todosCanceladosPorPedido = new Map<string, boolean>();
        ((itensData || []) as unknown as ItemQuadroRow[]).forEach((it) => {
          pedidosComItem.add(it.pedido_id);
          const anterior = todosCanceladosPorPedido.get(it.pedido_id) ?? true;
          todosCanceladosPorPedido.set(it.pedido_id, anterior && it.quadro_atual === 'PEDIDO_CANCELADO');
          // (Douglas 04/08) Pedido VIVO = tem item fora de COMERCIAL e de
          // PEDIDO_CANCELADO. O lead que virou esse pedido já cumpriu a função e
          // NÃO é oportunidade — não pode ser reaproveitado aqui.
          if (it.quadro_atual !== 'COMERCIAL' && it.quadro_atual !== 'PEDIDO_CANCELADO') {
            pedidosVivos.add(it.pedido_id);
          }
        });
        todosCanceladosPorPedido.forEach((cancelado, pedidoId) => {
          if (cancelado) pedidosCancelados.add(pedidoId);
        });
      }
      const leadsAbertos = leadsAbertosRaw.filter((l) => {
        // Lead que já virou pedido vivo sai da lista: o sistema mandava o consultor
        // de volta pra ele em vez de criar o pedido novo, e ele ficava preso. Foi o
        // caso do lead PAIM — 3 tentativas em 8 minutos. (Douglas 04/08)
        if (l.pedido_id && pedidosVivos.has(l.pedido_id)) return false;
        // ⛔ PEDIDO SEM NENHUM ITEM = CASCA VAZIA, e casca vazia nao segura ninguem.
        // (Eduarda 21/08/2026) O lead antigo do M.M. — TAB, NEGOCIANDO — apontava
        // para um pedido com ZERO itens. Sem item ele nao era "vivo" nem "cancelado",
        // escapava dos dois filtros, ganhava a prioridade de NEGOCIANDO e o sistema
        // respondia "Lead ja existe na sua carteira" em vez de criar o manual. Eram
        // 22 leads assim, 21 sequestrando a criacao — 21 CNPJs travados.
        if (leituraDeItensOk && l.pedido_id && !pedidosComItem.has(l.pedido_id)) return false;
        // Cancelado de OUTRO consultor também fica de fora (LEI 03/08).
        return !(
          l.pedido_id && pedidosCancelados.has(l.pedido_id)
          && l.consultor_id && l.consultor_id !== userProfile.id
        );
      });

      const PRIORIDADE_STATUS: Record<string, number> = { PROPOSTA: 5, NEGOCIANDO: 4, TENTANDO_CONTATO: 3, ATRIBUIDO: 2, DISPONIVEL: 1 };
      const leadExistente = leadsAbertos.slice().sort(
        (a, b) => (PRIORIDADE_STATUS[b.status_lead] || 0) - (PRIORIDADE_STATUS[a.status_lead] || 0)
      )[0] || null;

      // REGRA DE EQUIPE: reaproveitar/abrir o lead existente se for o proprio dono OU
      // se gerencia o dono pela hierarquia (supervisor da equipe, diretoria...).
      let podeGerenciarExistente = false;
      if (leadExistente?.consultor_id) {
        if (leadExistente.consultor_id === userProfile.id) {
          podeGerenciarExistente = true;
        } else {
          const { data: pgData } = await supabase.rpc('crm_pode_gerenciar_lead', {
            p_user_id: userProfile.id,
            p_lead_consultor_id: leadExistente.consultor_id,
          });
          podeGerenciarExistente = pgData === true;
        }
      }

      if (leadExistente) {
        if (destinoRetencaoId && leadExistente.consultor_id && leadExistente.consultor_id !== destinoRetencaoId
            && leadExistente.status_lead !== 'DISPONIVEL') {
          // Retenção não tem regra de dono: o lead aberto de outro consultor passa
          // para ela no estágio em que está, e o pedido segue o lead.
          const { error: moverErro } = await supabase
            .from('leads_carteira')
            .update({
              consultor_id: destinoRetencaoId,
              equipe_id: equipeIdFinal,
              atribuido_por: userProfile.id,
              data_atribuicao: now.toISOString(),
            })
            .eq('id', leadExistente.id);
          if (moverErro) throw moverErro;
          const { data: conferido } = await supabase
            .from('leads_carteira')
            .select('consultor_id')
            .eq('id', leadExistente.id)
            .maybeSingle();
          if ((conferido as { consultor_id?: string | null } | null)?.consultor_id !== destinoRetencaoId) {
            throw new Error('O lead aberto deste CNPJ não passou para o Consultor Retenção. Avise a gestão com o número do lead.');
          }
          await supabase.from('leads_carteira_historico').insert({
            lead_id: leadExistente.id,
            acao: 'TRANSFERIDO',
            usuario_id: userProfile.id,
            observacao: `Lead passou para o Consultor Retenção por ${userProfile.nome} (retenção não tem regra de dono)`,
          });
          toast({
            title: 'Lead passou para o Consultor Retenção',
            description: `O lead aberto de ${dados.razao_social} agora é do Consultor Retenção. O dono do cliente não muda.`,
          });
          onOpenChange(false);
          onSuccess?.(leadExistente.id);
          setSalvando(false);
          return;
        }
        if (!leadExistente.consultor_id || leadExistente.status_lead === 'DISPONIVEL') {
          // Lead existe mas está disponível — atualizar em vez de criar novo
          const { error: updateError } = await supabase
            .from('leads_carteira')
            .update({
              // LEI: criar lead manual NÃO é trabalho → nasce DISPONIVEL (com o
              // consultor). Vira NEGOCIANDO quando ele trabalhar (email/whats/tabulação).
              status_lead: 'DISPONIVEL',
              status_prospeccao: 'NEGOCIACAO',
              consultor_id: consultorIdFinal,
              equipe_id: equipeIdFinal,
              data_atribuicao: now.toISOString(),
              data_recebimento: now.toISOString(),
              contato_real_encontrado: true,
              contato_real_nome: contatoNome.trim(),
              contato_real_telefone: contatoTelefone.replace(/\D/g, ''),
              contato_real_cargo: contatoCargo.trim(),
              contato_real_email: contatoEmail.trim().toLowerCase(),
              data_contato_encontrado: now.toISOString(),
              qtd_linhas_voz: linhas ? parseInt(linhas) : null,
              valor_fatura_atual: valorAtual ? parseFloat(valorAtual.replace(',', '.')) : null,
              data_limite_contato: new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString(),
              data_limite_pedido: new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString(),
              operadora_atual: operadoraAtualSel || null,
              observacoes_consultor: `${origemObs}${interesseSel ? ` — Interesse: ${interesseSel}` : ''}${obsLivre.trim() ? ` — ${obsLivre.trim()}` : ''}`,
            })
            .eq('id', leadExistente.id);

          if (updateError) throw updateError;

          const consultorNome = direcionadoAoDono?.nome 
            || (isVendedor ? userProfile.nome : consultores.find(c => c.id === consultorIdFinal)?.nome || 'Consultor');

          await supabase.from('leads_carteira_historico').insert({
            lead_id: leadExistente.id,
            acao: 'CRIADO_MANUAL',
            usuario_id: userProfile.id,
            observacao: `Lead existente (DISPONÍVEL) atualizado manualmente por ${userProfile.nome} e atribuído a ${consultorNome} (Tipo: ${tipoLeadSelecionado})`
          });

          toast({
            title: 'Lead atribuído com sucesso!',
            description: `Lead existente para ${dados.razao_social} foi atribuído a ${consultorNome}.`,
          });

          onOpenChange(false);
          onSuccess?.(leadExistente.id);
          setSalvando(false);
          return;
        } else if (podeGerenciarExistente) {
          const statusAtivo = ['ATRIBUIDO', 'NEGOCIANDO'].includes(leadExistente.status_lead);
          if (statusAtivo) {
            // Lead ainda aberto — redirecionar
            toast({
              title: 'Lead já existe na sua carteira',
              description: `Você já possui um lead de ${tipoLeadSelecionado} ativo para este CNPJ neste mês. Abrindo o lead existente.`,
            });
            onOpenChange(false);
            onSuccess?.(leadExistente.id);
            setSalvando(false);
            return;
          }
          // Lead encerrado — este cenário não deve ocorrer pela nova busca (filtra finalizados)
          // mas caso chegue aqui, redirecionar para o lead existente
          toast({
            title: 'Lead já existe na sua carteira',
            description: `Você já possui um lead ativo para este CNPJ na campanha MANUAL. Abrindo o lead existente.`,
          });
          onOpenChange(false);
          onSuccess?.(leadExistente.id);
          setSalvando(false);
          return;
        } else {
          // Douglas 16/06: NÃO bloquear quando já existe lead (era falso-positivo —
          // pegava o próprio lead carteirizado/ativo do consultor e travava). Em vez
          // de erro, ABRE o lead existente pra ele seguir sem sair da tela.
          toast({
            title: 'Lead em andamento por outro consultor',
            description: `Este CNPJ ja tem um lead (${leadExistente.status_lead}) de outro consultor. Solicite a transferencia ao seu gestor.`,
            variant: 'destructive',
          });
          setSalvando(false);
          return;
        }
      }

      // Sem lead em aberto → criar novo (REGRA 2: exceção se havia finalizado, o novo INSERT passa pela partial index)
      const { data: novoLead, error } = await supabase
        .from('leads_carteira')
        .insert({
          cnpj: isPF ? null : cnpjLimpo,
          cpf: isPF ? cnpjLimpo : null,
          tipo_pessoa: tipoPessoa,
          razao_social: dados.razao_social,
          uf: dados.endereco_uf,
          endereco_empresa: endereco || null,
          telefone: dados.telefone_principal?.replace(/\D/g, '') || null,
          email: dados.email_principal || null,
          nome_adm: dados.socios?.[0]?.nome || null,
          
          tipo_lead: tipoLeadSelecionado,
          origem_lead: 'MANUAL',
          campanha_id: campanhaManualId, // REGRA 3: sempre vincular campanha MANUAL
          
          status_aptidao: 'APTO',
          // LEI: criar lead manual NÃO é trabalho → nasce DISPONIVEL (com o consultor).
          status_lead: 'DISPONIVEL',
          status_prospeccao: 'NEGOCIACAO',
          e_cliente_carteira: false,
          
          contato_real_encontrado: true,
          contato_real_nome: contatoNome.trim(),
          contato_real_telefone: contatoTelefone.replace(/\D/g, ''),
          contato_real_cargo: contatoCargo.trim(),
          contato_real_email: contatoEmail.trim().toLowerCase(),
          data_contato_encontrado: now.toISOString(),
          
          qtd_linhas_voz: linhas ? parseInt(linhas) : null,
          valor_fatura_atual: valorAtual ? parseFloat(valorAtual.replace(',', '.')) : null,

          consultor_id: consultorIdFinal,
          equipe_id: equipeIdFinal,
          data_atribuicao: now.toISOString(),
          data_recebimento: now.toISOString(),
          
          data_limite_contato: new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          data_limite_pedido: new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          
          mes_referencia: now.toISOString().slice(0, 10),
          operadora_atual: operadoraAtualSel || null,
          observacoes_consultor: `${origemObs}${interesseSel ? ` — Interesse: ${interesseSel}` : ''}${obsLivre.trim() ? ` — ${obsLivre.trim()}` : ''}`,
          importacao_id: importacaoId,
        })
        .select('id')
        .single();

      if (error) throw error;

      // Registrar histórico
      const consultorNome = direcionadoAoDono?.nome 
        || (isVendedor ? userProfile.nome : consultores.find(c => c.id === consultorIdFinal)?.nome || 'Consultor');
        
      await supabase.from('leads_carteira_historico').insert({
        lead_id: novoLead.id,
        acao: 'CRIADO_MANUAL',
        usuario_id: userProfile.id,
        observacao: direcionadoAoDono
          ? `Lead criado por ${userProfile.nome} e direcionado ao Dono Comercial ${direcionadoAoDono.nome} (Fluxo Oficial) (Tipo: ${tipoLeadSelecionado})`
          : `Lead criado manualmente por ${userProfile.nome} e atribuído a ${consultorNome} (Tipo: ${tipoLeadSelecionado})`
      });

      toast({
        title: 'Lead criado com sucesso!',
        description: direcionadoAoDono
          ? `${dados.razao_social} direcionado ao Dono Comercial: ${direcionadoAoDono.nome}.`
          : `${dados.razao_social} atribuído a ${consultorNome}.`,
      });

      onOpenChange(false);
      onSuccess?.(novoLead.id);

    } catch (err: any) {
      console.error('Erro ao criar lead:', err);
      const isRLS = err?.message?.includes('row-level security');
      const isDuplicate = err?.message?.includes('idx_leads_campanha_cnpj_aberto'); // so o indice REAL da campanha = 'ja existe'; outros erros mostram a msg verdadeira (17/06)
      
      let title = 'Erro ao criar lead';
      let description = err?.message || 'Tente novamente ou contate o suporte.';
      let variant: 'default' | 'destructive' = 'destructive';

      if (isDuplicate) {
        title = 'Lead já existe na campanha MANUAL';
        description = 'Regra CRM nº 2: já existe um lead em aberto para este CNPJ na campanha MANUAL.';
      } else if (isRLS) {
        title = 'Este CNPJ pertence a outro consultor';
        description = 'Solicite a transferência ao seu gestor para poder criar leads para este cliente.';
        variant = 'default';
      }

      toast({ title, description, variant });
    } finally {
      setSalvando(false);
    }
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] w-full max-w-[672px] flex-col gap-0 overflow-hidden rounded-2xl p-0 [&>button.absolute]:hidden">
        {/* 67d — hero vinho: a identidade dos modais da casa (rodada 2: 12px 18px) */}
        <DialogHeader className="relative shrink-0 space-y-0 overflow-hidden bg-[linear-gradient(135deg,#4A1320_0%,#5B0A0D_55%,#6E1626_100%)] px-[18px] py-3">
          <span aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.12]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='120' height='120' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")" }} />
          <div className="relative flex items-center justify-between gap-3">
            <div className="min-w-0">
              <DialogTitle className="flex items-center gap-2 text-white">
                <Building2 className="h-[15px] w-[15px] text-white/60" />
                <span className="truncate font-display text-[17px] font-bold">Novo lead manual</span>
              </DialogTitle>
              <DialogDescription className="mt-0.5 text-[11px] text-white/70">
                tipo MAN · entra Disponível na sua carteira
              </DialogDescription>
            </div>
            <button
              type="button"
              className="inline-flex h-[28px] w-[28px] flex-shrink-0 items-center justify-center rounded-full border border-white/[0.18] bg-white/[0.14] text-white hover:bg-white/[0.24]"
              onClick={() => onOpenChange(false)}
              title="Fechar"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-[13px] overflow-y-auto px-[18px] py-3">
          {/* ETAPA 1: Buscar CNPJ */}
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <Search className="h-3 w-3 text-muted-foreground" />
              <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Comece pelo documento</span>
              <span className="h-px flex-1 bg-border" />
            </div>

            {etapa === 'busca' && (
              <div className="flex gap-1 rounded-lg bg-muted p-0.5" role="radiogroup" aria-label="Documento do cliente">
                {([['PJ', 'CNPJ', 'pessoa jurídica'], ['PF', 'CPF', 'pessoa física']] as const).map(([value, rotulo, sub]) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={tipoPessoa === value}
                    disabled={loadingDocumento}
                    className={cn('flex h-9 flex-1 items-center justify-center gap-1.5 rounded-[7px] text-[12px] transition-colors', tipoPessoa === value ? 'bg-card font-bold text-primary shadow-[0_1px_3px_rgba(60,20,30,0.10)]' : 'font-medium text-muted-foreground hover:text-foreground')}
                    onClick={() => {
                      if (tipoPessoa === value) return;
                      setTipoPessoa(value); setCnpjInput(''); limparResultado(); setDiagnostico(null);
                      setBloqueio(null);
      setAlertaCpf(null);
    setAlertaCpf(null); setLeadEncontrado(null); setDonoComercial(null); setConsultorTravado(false);
                      setEditRazaoSocial(''); setEditNomeFantasia(''); setEditCidade(''); setEditUF(''); setEditCEP('');
                      setContatoNome(''); setContatoTelefone(''); setContatoCargo(''); setContatoEmail('');
                      setOrigemDadosCpf(null);
                    }}
                  >
                    {value === 'PF' ? <User className="h-3.5 w-3.5" /> : <Building2 className="h-3.5 w-3.5" />}
                    {rotulo}
                    <span className="text-[10px] font-normal text-muted-foreground">{sub}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Toggle entre Buscar na Carteira e Novo CNPJ */}
            {etapa === 'busca' && (
              <div className="flex gap-1 rounded-lg bg-muted p-0.5">
                <button
                  type="button"
                  className={cn('flex h-7 flex-1 items-center justify-center gap-1.5 rounded-[7px] text-[11px] transition-colors', modoBusca === 'carteira' ? 'bg-card font-bold text-primary shadow-[0_1px_3px_rgba(60,20,30,0.10)]' : 'font-medium text-muted-foreground hover:text-foreground')}
                  onClick={() => setModoBusca('carteira')}
                >
                  <Database className="h-3 w-3" />
                  Buscar na Carteira
                </button>
                <button
                  type="button"
                  className={cn('flex h-7 flex-1 items-center justify-center gap-1.5 rounded-[7px] text-[11px] transition-colors', modoBusca === 'cnpj' ? 'bg-card font-bold text-primary shadow-[0_1px_3px_rgba(60,20,30,0.10)]' : 'font-medium text-muted-foreground hover:text-foreground')}
                  onClick={() => setModoBusca('cnpj')}
                >
                  <Search className="h-3 w-3" />
                  Novo {documentoLabel}
                </button>
              </div>
            )}

            {/* Modo Carteira: busca por nome/cnpj */}
            {modoBusca === 'carteira' && etapa === 'busca' && (
              <div className="space-y-2">
                <Input
                  placeholder={isPF ? 'Digite nome ou CPF...' : 'Digite razão social, fantasia ou CNPJ...'}
                  value={buscaCarteira}
                  onChange={(e) => setBuscaCarteira(e.target.value)}
                />
                <div className="border rounded-lg max-h-64 overflow-y-auto">
                    {loadingCarteira ? (
                      <div className="p-3 text-center">
                        <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                      </div>
                    ) : clientesFiltrados.length === 0 ? (
                      <div className="p-3 text-center text-muted-foreground text-xs">
                        Nenhum cliente encontrado
                      </div>
                    ) : (
                      clientesFiltrados.map(c => (
                        <button
                          key={c.id}
                          onClick={() => handleSelecionarClienteCarteira(c)}
                          className="w-full text-left px-3 py-2 hover:bg-muted/50 border-b last:border-0 text-sm transition-colors"
                        >
                          <div className="font-medium truncate">{c.razao_social || c.nome_fantasia || 'Sem nome'}</div>
                          <div className="text-xs text-muted-foreground">
                            {c.cpf ? formatarCPF(c.cpf) : formatarCNPJ(c.cnpj)} {c.classificacao_cliente ? `· ${c.classificacao_cliente}` : ''}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
              </div>
            )}

            {/* Modo CNPJ: input compacto + Consultar; em etapa dados vira leitura com o link de trocar */}
            {modoBusca === 'cnpj' && (
              <div>
                <div className="flex items-center gap-2">
                  <Label className="text-[9.5px] font-bold uppercase tracking-[0.8px] text-muted-label">{documentoLabel}</Label>
                  {etapa === 'dados' && (
                    <button
                      type="button"
                      className="text-[11px] text-primary underline-offset-2 hover:underline dark:text-[#C87484]"
                      onClick={() => {
                        setEtapa('busca');
                        limparResultado();
                        setCnpjInput('');
                        setTipoLeadSelecionado('MANUAL');
                        setBloqueio(null);
      setAlertaCpf(null);
    setAlertaCpf(null);
                        setDonoComercial(null);
                        setConsultorTravado(false);
                        setConsultorSelecionadoId('');
                        setBuscaCarteira('');
                        setEditRazaoSocial('');
                        setEditNomeFantasia('');
                        setEditCidade('');
                        setEditUF('');
                        setEditCEP('');
                      }}
                    >
                      Buscar outro {documentoLabel}
                    </button>
                  )}
                </div>
                <div className="mt-1 flex gap-2">
                  <Input
                    placeholder={isPF ? '000.000.000-00' : '00.000.000/0001-00'}
                    value={cnpjInput}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '');
                      setCnpjInput(val.length <= (isPF ? 11 : 14) ? (isPF ? formatarCPF(val) : formatarCNPJ(val)) : cnpjInput);
                    }}
                    disabled={loadingDocumento || etapa === 'dados'}
                    className="h-[30px] flex-1 text-[11.5px]"
                  />
                  {etapa === 'busca' && (
                    <button
                      type="button"
                      onClick={handleBuscarCNPJ}
                      disabled={loadingDocumento || cnpjInput.replace(/\D/g, '').length !== (isPF ? 11 : 14)}
                      className="flex h-[30px] shrink-0 items-center gap-1.5 rounded-lg bg-[linear-gradient(90deg,#4A1320,#5B0A0D)] px-3 text-[11.5px] font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-50 dark:bg-[linear-gradient(90deg,#9D2530,#6C2236)]"
                    >
                      {loadingDocumento ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Search className="h-3 w-3" /> Consultar</>}
                    </button>
                  )}
                </div>
              </div>
            )}

          </div>

          {/* Rodada 2: o retorno da consulta é UMA faixa com filete — cada caso
              de duplicidade vira uma LINHA com a ação inline, nunca card. */}
          {etapa === 'dados' && dados && (() => {
            const d: any = diagnostico?.detalhes || {};
            const ativo = !!d.tem_pedido_ativo;
            const andamento = !!d.tem_pedido_andamento;
            const temDono = !!d.tem_dono_comercial;
            const carteiraLabel = ativo ? 'Carteira ativa' : (andamento ? 'Carteira — pedido em andamento' : 'Carteira (sem pedido ativo)');
            const leadMeu = leadEncontrado?.consultor_id === userProfile?.id;
            const semDuplicidade = diagnostico && !temDono && !andamento && !ativo && !leadEncontrado && !bloqueio;
            return (
              <div className="relative overflow-hidden rounded-r-lg rounded-l-none border border-border bg-card pl-1">
                <span aria-hidden className="absolute inset-y-0 left-0 w-1 bg-[linear-gradient(180deg,#6E1626,#4E1522_55%,#3E0E1B)]" />
                <div className="divide-y divide-[#F0E9E1] dark:divide-border/60">
                  {/* linha da consulta — dot verde: desfecho bom */}
                  <div className="flex min-h-[30px] flex-wrap items-center gap-x-2 gap-y-0.5 px-2.5 py-1">
                    {dadosManuaisEditaveis ? (
                      <>
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#7A2235] dark:bg-[#C87484]" />
                        <span className="text-[12px] font-semibold text-[#7A2235] dark:text-[#C87484]">{isPF ? 'CPF sem cadastro na Assertiva — complete os campos abaixo' : 'CNPJ não localizado na Receita — complete os campos abaixo'}</span>
                      </>
                    ) : (
                      <>
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#075E54] dark:bg-[#25D366]" />
                        <span className="text-[12px] font-semibold text-[#075E54] dark:text-[#25D366]">{isPF ? (origemDadosCpf === 'crm' ? 'CPF já cadastrado no CRM — dados preenchidos' : 'CPF validado na Assertiva — dados preenchidos automaticamente') : 'Receita encontrada — dados preenchidos automaticamente'}</span>
                      </>
                    )}
                    {semDuplicidade && <span className="text-[10.5px] text-muted-foreground">· sem lead aberto para este {documentoLabel}</span>}
                  </div>

                  {/* linha da carteira / dono — dot bordô claro: é trava de dono */}
                  {(temDono || andamento || ativo) && (
                    <div className="px-2.5 py-1">
                      <div className="flex min-h-[22px] flex-wrap items-center gap-x-2 gap-y-0.5">
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#7A2235] dark:bg-[#C87484]" />
                        <span className="text-[12px] font-semibold text-card-foreground">
                          {carteiraLabel}{d.owner_nome ? ` · dono ${d.owner_nome}` : ''}{ativo ? ' · pedido ativo' : ''}
                        </span>
                      </div>
                      {destinoRetencaoId && !bloqueio ? (
                        <p className="pl-3.5 text-[10.5px] text-muted-foreground">fica com o Consultor Retenção · o dono do cliente não muda</p>
                      ) : donoComercial && !bloqueio && (
                        <p className="pl-3.5 text-[10.5px] text-muted-foreground">será atribuído exclusivamente ao dono · fluxo oficial</p>
                      )}
                    </div>
                  )}

                  {alertaCpf && !bloqueio && (
                    <div className="flex min-h-[30px] flex-wrap items-center gap-x-2 gap-y-0.5 px-2.5 py-1">
                      <ShieldAlert className="h-3 w-3 shrink-0 text-warning" />
                      <span className="text-[11px] font-bold text-warning">Atenção — CPF irregular</span>
                      <span className="min-w-0 flex-1 truncate text-[10.5px] text-muted-foreground" title={alertaCpf}>{alertaCpf}</span>
                    </div>
                  )}

                  {/* linha do bloqueio */}
                  {bloqueio && (
                    <div className="flex min-h-[30px] flex-wrap items-center gap-x-2 gap-y-0.5 px-2.5 py-1">
                      <ShieldAlert className="h-3 w-3 shrink-0 text-destructive" />
                      <span className="text-[11px] font-bold text-destructive">
                        {bloqueio.tipo === 'DONO_OUTRO_CONSULTOR' ? 'Bloqueado — cliente de outra carteira' : bloqueio.tipo === 'CPF_IRREGULAR' ? 'Bloqueado — CPF com óbito' : 'Bloqueado — pedido em andamento'}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-[10.5px] text-muted-foreground" title={bloqueio.mensagem}>{bloqueio.mensagem}</span>
                      {bloqueio.tipo === 'DONO_OUTRO_CONSULTOR' && diagnostico?.detalhes?.cliente_id && (
                        <button
                          type="button"
                          className="flex h-6 shrink-0 items-center gap-1 rounded-lg border border-border bg-card px-2 text-[10.5px] font-semibold text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
                          onClick={() => setSolicitarAberto(true)}
                        >
                          <ArrowRightLeft className="h-3 w-3" /> Solicitar transferência
                        </button>
                      )}
                    </div>
                  )}

                  {/* linha do lead aberto — dot muted: informação; chips neutros no status */}
                  {leadEncontrado && (
                    <div className="flex min-h-[30px] flex-wrap items-center gap-x-2 gap-y-0.5 px-2.5 py-1">
                      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/40" />
                      <span className="text-[12px] font-semibold text-card-foreground">
                        {leadMeu ? 'Você já tem um lead aberto' : podeGerenciarLead ? 'Lead aberto da equipe' : 'Lead de outro consultor'}
                      </span>
                      {[leadEncontrado.status_lead, leadEncontrado.tipo_lead].filter(Boolean).map((tag) => (
                        <span key={String(tag)} className="flex h-[18px] shrink-0 items-center rounded-full bg-[#F2EEE8] px-2 text-[9.5px] font-bold text-muted-foreground dark:bg-white/[0.06]">
                          {String(tag)}
                        </span>
                      ))}
                      {(leadMeu || podeGerenciarLead) ? (
                        <button
                          type="button"
                          className="ml-auto flex h-[26px] shrink-0 items-center rounded-lg border border-border bg-card px-2.5 text-[10.5px] font-semibold text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
                          onClick={() => { onOpenChange(false); onSuccess?.(leadEncontrado.id); }}
                        >
                          {leadMeu ? 'Abrir o meu lead' : 'Abrir o lead'}
                        </button>
                      ) : (
                        <span className="ml-auto text-[10.5px] text-muted-foreground">solicite a transferência ao seu gestor</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* ETAPA 2 — seções compactas (campos 30px, rótulos 9,5px, hairline) */}
          {etapa === 'dados' && dados && !bloqueio && (
            <>
              {/* EMPRESA — a Receita preenche; o usuário completa/ajusta aqui */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Building2 className="h-3 w-3 text-muted-foreground" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">{isPF ? 'Cliente' : 'Empresa'}</span>
                  <span className="h-px flex-1 bg-border" />
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                  <div>
                    <Label className="text-[9.5px] font-bold uppercase tracking-[0.8px] text-muted-label">{isPF ? 'Nome completo' : 'Razão social'}</Label>
                    <Input value={editRazaoSocial} onChange={(e) => setEditRazaoSocial(e.target.value)} placeholder={isPF ? 'Nome completo' : 'Razão social'} className="mt-1 h-[30px] text-[11.5px]" />
                  </div>
                  {!isPF && <div>
                    <Label className="text-[9.5px] font-bold uppercase tracking-[0.8px] text-muted-label">Nome fantasia</Label>
                    <Input value={editNomeFantasia} onChange={(e) => setEditNomeFantasia(e.target.value)} placeholder="opcional" className="mt-1 h-[30px] text-[11.5px]" />
                  </div>}
                </div>
                <div className="grid grid-cols-[72px_1fr_120px] gap-x-3">
                  <div>
                    <Label className="text-[9.5px] font-bold uppercase tracking-[0.8px] text-muted-label">UF</Label>
                    <Select value={editUF} onValueChange={setEditUF}>
                      <SelectTrigger className="mt-1 h-[30px] text-[11.5px]"><SelectValue placeholder="UF" /></SelectTrigger>
                      <SelectContent>
                        {['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'].map(uf => (
                          <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[9.5px] font-bold uppercase tracking-[0.8px] text-muted-label">Cidade</Label>
                    <Input value={editCidade} onChange={(e) => setEditCidade(e.target.value)} placeholder="Cidade" className="mt-1 h-[30px] text-[11.5px]" />
                  </div>
                  <div>
                    <Label className="text-[9.5px] font-bold uppercase tracking-[0.8px] text-muted-label">CEP</Label>
                    <Input value={editCEP} onChange={(e) => setEditCEP(e.target.value)} placeholder="00000-000" className="mt-1 h-[30px] text-[11.5px]" />
                  </div>
                </div>
              </div>

              {/* Consultor responsável (gestores) — travado se tem Dono Comercial */}
              {!isVendedor && (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <Users className="h-3 w-3 text-muted-foreground" />
                    <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Consultor responsável</span>
                    {!consultorSelecionadoId && <span className="text-[10px] text-destructive">obrigatório</span>}
                    {loadingConsultores && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
                    <span className="h-px flex-1 bg-border" />
                  </div>
                  {consultorTravado && donoComercial ? (
                    <div className="flex gap-1.5">
                      {[
                        { id: donoComercial.id, nome: donoComercial.nome, rotulo: 'Dono Comercial' },
                        ...(retencao && retencao.id !== donoComercial.id
                          ? [{ id: retencao.id, nome: retencao.nome, rotulo: 'Sem regra de dono' }]
                          : []),
                      ].map((op) => {
                        const escolhido = consultorSelecionadoId === op.id;
                        return (
                          <button
                            key={op.id}
                            type="button"
                            onClick={() => {
                              setConsultorSelecionadoId(op.id);
                              setEquipeSelecionadaId(consultores.find((c) => c.id === op.id)?.equipe_id || null);
                            }}
                            className={cn(
                              'flex h-[30px] min-w-0 flex-1 items-center gap-2 rounded-lg border px-2.5 text-left',
                              escolhido ? 'border-primary/40 bg-primary/5' : 'border-border bg-card hover:bg-[#F4F0EA] dark:hover:bg-[#2A1F1F]',
                            )}
                          >
                            {escolhido
                              ? <Lock className="h-3 w-3 shrink-0 text-primary dark:text-[#C87484]" />
                              : <span className="h-2 w-2 shrink-0 rounded-full border-[1.5px] border-muted-foreground" />}
                            <span className={cn('truncate text-[11.5px] font-medium', escolhido ? 'text-primary dark:text-[#C87484]' : 'text-card-foreground')} title={op.nome}>{op.nome}</span>
                            <span className="ml-auto shrink-0 whitespace-nowrap text-[9.5px] font-bold uppercase tracking-[0.8px] text-muted-foreground">{op.rotulo}</span>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <Select
                      value={consultorSelecionadoId}
                      onValueChange={(id) => {
                        setConsultorSelecionadoId(id);
                        const consultor = consultores?.find(c => c.id === id);
                        setEquipeSelecionadaId(consultor?.equipe_id || null);
                      }}
                    >
                      <SelectTrigger className="h-[30px] w-full text-[11.5px]">
                        <SelectValue placeholder="Selecione o consultor responsável..." />
                      </SelectTrigger>
                      <SelectContent>
                        {consultores?.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            <div className="flex items-center gap-2">
                              <Avatar className="h-5 w-5">
                                <AvatarImage src={c.foto_url || undefined} />
                                <AvatarFallback className="text-[10px] bg-primary/10">{c.nome?.[0]}</AvatarFallback>
                              </Avatar>
                              <span className="text-[11.5px]">{c.nome}</span>
                              <span className="ml-auto text-[9.5px] font-bold uppercase tracking-[0.8px] text-muted-foreground">{c.equipe_nome}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              {/* CONTATO */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <User className="h-3 w-3 text-muted-foreground" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Contato</span>
                  {!contatoEncontradoCRM && <span className="text-[10px] text-destructive">obrigatório</span>}
                  {loadingContato && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
                  <span className="h-px flex-1 bg-border" />
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                  <div>
                    <Label className="text-[9.5px] font-bold uppercase tracking-[0.8px] text-muted-label">Nome do contato</Label>
                    <Input placeholder="Nome do contato" value={contatoNome} onChange={(e) => setContatoNome(e.target.value)} className="mt-1 h-[30px] text-[11.5px]" title={contatoEncontradoCRM ? 'Contato encontrado no CRM' : undefined} />
                  </div>
                  <div>
                    <Label className="text-[9.5px] font-bold uppercase tracking-[0.8px] text-muted-label">Cargo</Label>
                    <Input placeholder="Ex: Sócio / decisor" value={contatoCargo} onChange={(e) => setContatoCargo(e.target.value)} className="mt-1 h-[30px] text-[11.5px]" />
                  </div>
                  <div>
                    <Label className="text-[9.5px] font-bold uppercase tracking-[0.8px] text-muted-label">Telefone</Label>
                    <Input placeholder="(00) 00000-0000" value={contatoTelefone} onChange={(e) => handleTelefoneChange(e.target.value)} className="mt-1 h-[30px] text-[11.5px]" />
                  </div>
                  <div>
                    <Label className="text-[9.5px] font-bold uppercase tracking-[0.8px] text-muted-label">E-mail</Label>
                    <Input type="email" placeholder="email@empresa.com" value={contatoEmail} onChange={(e) => setContatoEmail(e.target.value)} className="mt-1 h-[30px] text-[11.5px]" />
                  </div>
                </div>
              </div>

              {/* SITUAÇÃO ATUAL — 1 linha de 4 campos */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Phone className="h-3 w-3 text-muted-foreground" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Situação atual</span>
                  <span className="text-[10px] text-muted-foreground">alimenta a análise da aba DADOS</span>
                  <span className="h-px flex-1 bg-border" />
                </div>
                <div className="grid grid-cols-[1fr_86px_110px_1.2fr] gap-x-3">
                  <div>
                    <Label className="text-[9.5px] font-bold uppercase tracking-[0.8px] text-muted-label">Operadora atual</Label>
                    <Select value={operadoraAtualSel} onValueChange={setOperadoraAtualSel}>
                      <SelectTrigger className="mt-1 h-[30px] text-[11.5px]"><SelectValue placeholder="Selecionar..." /></SelectTrigger>
                      <SelectContent>
                        {['VIVO', 'TIM', 'CLARO', 'OI', 'ALGAR', 'OUTRA'].map((op) => (
                          <SelectItem key={op} value={op}>{op}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[9.5px] font-bold uppercase tracking-[0.8px] text-muted-label">Linhas móveis</Label>
                    <Input type="number" placeholder="4" value={linhas} onChange={(e) => setLinhas(e.target.value)} className="mt-1 h-[30px] text-[11.5px]" />
                  </div>
                  <div>
                    <Label className="text-[9.5px] font-bold uppercase tracking-[0.8px] text-muted-label">Fatura atual (R$)</Label>
                    <Input placeholder="289,90" value={valorAtual} onChange={(e) => setValorAtual(e.target.value)} className="mt-1 h-[30px] text-[11.5px]" />
                  </div>
                  <div>
                    <Label className="text-[9.5px] font-bold uppercase tracking-[0.8px] text-muted-label">Interesse</Label>
                    <Select value={interesseSel} onValueChange={setInteresseSel}>
                      <SelectTrigger className="mt-1 h-[30px] text-[11.5px]"><SelectValue placeholder="Selecionar..." /></SelectTrigger>
                      <SelectContent>
                        {['Portabilidade', 'Portabilidade + fibra', 'Fibra', 'Renovação', 'Serviços digitais / TI', 'Outro'].map((op) => (
                          <SelectItem key={op} value={op}>{op}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-[9.5px] font-bold uppercase tracking-[0.8px] text-muted-label">Observação</Label>
                  <textarea
                    placeholder="contexto da indicação, melhor horário, quem pediu..."
                    value={obsLivre}
                    onChange={(e) => setObsLivre(e.target.value)}
                    className="mt-1 min-h-[52px] w-full resize-y rounded-md border border-input bg-background px-2.5 py-1.5 text-[11.5px] outline-none placeholder:text-muted-foreground focus:ring-1 focus:ring-ring"
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {/* rodapé: estado dos obrigatórios + Criar lead e abrir */}
        <div className="flex shrink-0 items-center justify-between gap-3 border-t bg-[#FBFAF9] px-5 py-3 dark:bg-white/[0.02]">
          <span className="min-w-0 truncate text-[10.5px] text-[#A18C78]">
            {dados ? (dadosValidos() ? `${documentoLabel} consultado · campos obrigatórios preenchidos` : `${documentoLabel} consultado · faltam campos obrigatórios`) : 'consulte o documento para começar'}
          </span>
          <div className="flex flex-shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={salvando}
              className="flex h-[30px] items-center rounded-lg border border-border bg-card px-3 text-[11.5px] font-semibold text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary disabled:opacity-50"
            >
              Cancelar
            </button>
            {etapa === 'dados' && !bloqueio && (
              <button
                type="button"
                onClick={handleSalvar}
                disabled={salvando || !dadosValidos()}
                className="flex h-[30px] items-center gap-1.5 rounded-lg bg-[linear-gradient(90deg,#4A1320,#5B0A0D)] px-3 text-[11.5px] font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-50 dark:bg-[linear-gradient(90deg,#9D2530,#6C2236)]"
              >
                {salvando ? (
                  <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Salvando...</>
                ) : (
                  <><CheckCircle className="h-3.5 w-3.5" /> Criar lead e abrir</>
                )}
              </button>
            )}
          </div>
        </div>
      </DialogContent>

      {solicitarAberto && diagnostico?.detalhes?.cliente_id && (
        <SolicitacaoAvaliacaoModal
          isOpen={solicitarAberto}
          onClose={() => setSolicitarAberto(false)}
          cliente={{
            id: diagnostico.detalhes.cliente_id,
            razao_social: dados?.razao_social ?? 'Cliente',
            cnpj: isPF ? formatarCPF(cnpjInput) : formatarCNPJ(diagnostico.detalhes.cnpj || ''),
          }}
          solicitanteId={userProfile?.id ?? ''}
          origem="lead_manual"
        />
      )}
    </Dialog>
  );
}
